<template>
  <div>
    <b-card>
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <h4 class="mb-0">Script Signal Provider</h4>
          <b-badge :variant="enabled ? 'light-success' : 'light-secondary'">
            {{ enabled ? 'AKTİF' : 'PASİF' }}
          </b-badge>
        </div>
      </template>

      <b-alert show variant="secondary" class="mb-2">
        <div class="p-1">
          <small>
            Onaylı monthly sim script'ini çalıştırıp <strong>giriş sinyallerini</strong> üretir ve
            runtime'a "entry provider" olarak verir. Çıkışlar (TP/SL/trail/step) canlı sistemde kalır.
            Yalnızca <strong>taze</strong> sinyaller açılır (hedef max-age kuralı); geçmiş girişler backfill edilmez.
          </small>
        </div>
      </b-alert>

      <!-- Durum özeti -->
      <b-alert :show="Boolean(config)" :variant="enabled ? 'success' : 'secondary'" class="mb-2">
        <div class="p-1">
          <div><strong>Son koşu:</strong> {{ config && config.lastRunAt ? formatTime(config.lastRunAt) : '—' }}</div>
          <div v-if="lastRunSummary"><strong>Son sonuç:</strong> {{ lastRunSummary }}</div>
        </div>
      </b-alert>

      <!-- Ayar formu -->
      <b-row>
        <b-col md="3">
          <b-form-group label="Ay (YYYY-MM)" description="Boş: içinde bulunulan ay">
            <b-form-input v-model="form.month" placeholder="2026-09" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Pencere günü">
            <b-form-input v-model.number="form.windowDays" type="number" min="1" max="31" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Target ID(ler)" description="Virgülle: 63 veya 63,64">
            <b-form-input v-model="form.targetIds" placeholder="63" />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Report prefix" description="Boş: script-signal-<ay>">
            <b-form-input v-model="form.reportPrefix" placeholder="opsiyonel" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group label="Env override (JSON)" description="Onaylı komut varsayılanlarını ezer. Örn: {&quot;BASELINE_VIRTUAL_RESCAN_MAX_POINTS&quot;:&quot;0&quot;}">
            <b-form-textarea v-model="form.envOverridesRaw" rows="3" placeholder="{}" />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-checkbox v-model="form.scriptOnly" switch>
            Sadece script sürsün (native produceSignal girişlerini sustur)
          </b-form-checkbox>
        </b-col>
      </b-row>

      <div class="d-flex flex-wrap mt-1" style="gap: 0.5rem;">
        <b-button variant="primary" :disabled="loading" @click="onSave">Kaydet</b-button>
        <b-button
          :variant="enabled ? 'outline-danger' : 'outline-success'"
          :disabled="loading"
          @click="onToggle"
        >
          {{ enabled ? 'Pasif Yap' : 'Aktive Et' }}
        </b-button>
        <b-button variant="success" :disabled="triggering" @click="onTrigger">
          <b-spinner v-if="triggering" small /> Tetikle (şimdi koştur)
        </b-button>
        <b-button variant="outline-secondary" @click="onRefreshEvents">Sinyalleri Yenile</b-button>
      </div>
    </b-card>

    <!-- Üretilen sinyaller -->
    <b-card title="Üretilen Script Sinyalleri">
      <!-- K/Z özeti -->
      <b-row class="mb-2 text-center">
        <b-col cols="6" md="3" class="mb-1">
          <div class="border rounded p-1">
            <div class="text-muted"><small>Toplam K/Z</small></div>
            <h4 class="mb-0" :class="pnlClass(pnlSummary.totalPnl)">
              {{ formatPnl(pnlSummary.totalPnl) }}
            </h4>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <div class="border rounded p-1">
            <div class="text-muted"><small>Kapanan / Açık</small></div>
            <h4 class="mb-0">{{ pnlSummary.closed }} <small class="text-info">/ {{ pnlSummary.openCount }}</small></h4>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <div class="border rounded p-1">
            <div class="text-muted"><small>Kazanan / Kaybeden</small></div>
            <h4 class="mb-0">
              <span class="text-success">{{ pnlSummary.wins }}</span>
              <small class="text-muted"> / </small>
              <span class="text-danger">{{ pnlSummary.losses }}</span>
            </h4>
          </div>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <div class="border rounded p-1">
            <div class="text-muted"><small>Başarı Oranı</small></div>
            <h4 class="mb-0">{{ pnlSummary.closed ? pnlSummary.winRate.toFixed(1) + '%' : '—' }}</h4>
          </div>
        </b-col>
      </b-row>

      <b-table
        :items="events"
        :fields="eventFields"
        responsive
        small
        show-empty
        empty-text="Henüz sinyal üretilmedi. 'Tetikle' ile koşabilir ya da otomatik koşuyu bekleyebilirsin."
      >
        <template #cell(side)="row">
          <b-badge :variant="row.item.side === 'LONG' ? 'light-success' : 'light-danger'">{{ row.item.side }}</b-badge>
        </template>
        <template #cell(status)="row">
          <b-badge :variant="statusVariant(row.item.status)">{{ row.item.status }}</b-badge>
        </template>
        <template #cell(signalTime)="row">{{ formatTime(row.item.signalTime) }}</template>
        <template #cell(entryTime)="row">{{ formatTime(row.item.entryTime) }}</template>
        <template #cell(realizedPnl)="row">
          <span v-if="row.item.realizedPnl != null" :class="pnlClass(row.item.realizedPnl)">
            {{ formatPnl(row.item.realizedPnl) }}
          </span>
          <b-badge v-else-if="row.item.consumedPositionId" variant="light-info">Açık</b-badge>
          <span v-else class="text-muted">—</span>
        </template>
        <template #cell(pnlPercent)="row">
          <span v-if="row.item.pnlPercent != null" :class="pnlClass(row.item.pnlPercent)">
            {{ formatPct(row.item.pnlPercent) }}
          </span>
          <span v-else class="text-muted">—</span>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BAlert, BBadge, BTable, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ScriptSignal',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BButton, BAlert, BBadge, BTable, BSpinner,
  },
  data() {
    return {
      form: {
        scriptOnly: false,
        month: '',
        windowDays: 5,
        targetIds: '',
        reportPrefix: '',
        envOverridesRaw: '',
      },
      eventFields: [
        { key: 'symbol', label: 'Sembol' },
        { key: 'timeframe', label: 'TF' },
        { key: 'side', label: 'Yön' },
        { key: 'indicator', label: 'İndikatör' },
        { key: 'signalTime', label: 'Sinyal Zamanı' },
        { key: 'entryTime', label: 'Giriş Zamanı' },
        { key: 'entryPrice', label: 'Fiyat' },
        { key: 'status', label: 'Durum' },
        { key: 'realizedPnl', label: 'PnL' },
        { key: 'pnlPercent', label: 'PnL %' },
      ],
      pollTimer: null,
      pollTicks: 0,
    }
  },
  computed: {
    config() { return this.$store.getters['scriptSignal/config'] },
    events() { return this.$store.getters['scriptSignal/events'] },
    loading() { return this.$store.getters['scriptSignal/loading'] },
    triggering() { return this.$store.getters['scriptSignal/triggering'] },
    enabled() { return this.$store.getters['scriptSignal/enabled'] },
    saveStatus() { return this.$store.getters['scriptSignal/dataSaveStatus'] },
    pnlSummary() {
      const rows = Array.isArray(this.events) ? this.events : []
      let closed = 0
      let openCount = 0
      let wins = 0
      let losses = 0
      let totalPnl = 0
      rows.forEach(r => {
        const raw = r.realizedPnl
        const pnl = raw == null ? null : Number(raw)
        if (pnl != null && Number.isFinite(pnl)) {
          // Pozisyon kapanmış: gerçekleşen PnL toplama girer.
          closed += 1
          totalPnl += pnl
          if (pnl > 0) wins += 1
          else if (pnl < 0) losses += 1
        } else if (r.consumedPositionId) {
          // Pozisyon açıldı ama henüz kapanmadı (PnL yok).
          openCount += 1
        }
      })
      const winRate = closed > 0 ? (wins / closed) * 100 : 0
      return {
        closed, openCount, wins, losses, totalPnl, winRate,
      }
    },
    lastRunSummary() {
      const r = this.config && this.config.lastRunResult
      if (!r || typeof r !== 'object') return null
      if (r.message === 'already_running') return 'Koşu sürüyor…'
      const parts = []
      if ('parsed' in r) parts.push(`${r.parsed} parse`)
      if ('inserted' in r) parts.push(`${r.inserted} yeni`)
      if ('seen' in r) parts.push(`${r.seen} tekrar`)
      if ('skipped' in r) parts.push(`${r.skipped} atlandı`)
      if (r.message && r.message !== 'already_running') parts.push(String(r.message))
      return parts.join(' · ') || (r.ok ? 'Tamam' : 'Hata')
    },
  },
  watch: {
    config: {
      immediate: true,
      handler(val) {
        if (!val) return
        this.form.scriptOnly = Boolean(val.scriptOnly)
        this.form.month = val.month || ''
        this.form.windowDays = val.windowDays || 5
        this.form.targetIds = Array.isArray(val.targetIds) ? val.targetIds.join(',') : ''
        this.form.reportPrefix = val.reportPrefix || ''
        this.form.envOverridesRaw = val.envOverrides && Object.keys(val.envOverrides).length
          ? JSON.stringify(val.envOverrides, null, 2)
          : ''
      },
    },
    saveStatus(val) {
      if (val.status === true) {
        this.showToast({ variant: 'success', title: 'Tamam', icon: 'CheckIcon', text: val.message })
      } else if (val.status === false) {
        this.showToast({ variant: 'danger', title: 'Hata', icon: 'XIcon', text: val.message })
      }
    },
  },
  created() {
    this.$store.dispatch('scriptSignal/getConfig')
    this.$store.dispatch('scriptSignal/getEvents')
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    parseEnvOverrides() {
      const raw = (this.form.envOverridesRaw || '').trim()
      if (!raw) return {}
      const parsed = JSON.parse(raw) // hata yukarıda yakalanır
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Env override bir JSON nesnesi olmalı')
      }
      const out = {}
      Object.keys(parsed).forEach(k => { out[k] = String(parsed[k]) })
      return out
    },
    async onSave() {
      let envOverrides
      try {
        envOverrides = this.parseEnvOverrides()
      } catch (e) {
        this.showToast({ variant: 'danger', title: 'Geçersiz JSON', icon: 'XIcon', text: e.message })
        return
      }
      const body = {
        scriptOnly: this.form.scriptOnly,
        month: this.form.month || undefined,
        windowDays: this.form.windowDays || undefined,
        targetIds: this.form.targetIds || undefined,
        reportPrefix: this.form.reportPrefix || undefined,
        envOverrides,
      }
      await this.$store.dispatch('scriptSignal/saveConfig', body).catch(() => {})
    },
    async onToggle() {
      await this.$store.dispatch('scriptSignal/toggleEnabled', !this.enabled)
    },
    async onTrigger() {
      await this.$store.dispatch('scriptSignal/trigger').catch(() => {})
      this.startPolling()
    },
    onRefreshEvents() {
      this.$store.dispatch('scriptSignal/getEvents')
      this.$store.dispatch('scriptSignal/getConfig')
    },
    startPolling() {
      this.stopPolling()
      this.pollTicks = 0
      this.pollTimer = setInterval(() => {
        this.pollTicks += 1
        this.$store.dispatch('scriptSignal/getConfig')
        this.$store.dispatch('scriptSignal/getEvents')
        if (this.pollTicks >= 24) this.stopPolling() // ~2 dk (5sn × 24)
      }, 5000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    statusVariant(status) {
      switch (status) {
        case 'CONSUMED': return 'light-success'
        case 'BLOCKED': return 'light-danger'
        case 'SKIPPED': return 'light-secondary'
        default: return 'light-warning'
      }
    },
    formatTime(value) {
      if (!value) return '—'
      const d = new Date(value)
      return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString()
    },
    pnlClass(value) {
      const n = Number(value)
      if (!Number.isFinite(n) || n === 0) return 'text-muted'
      return n > 0 ? 'text-success font-weight-bolder' : 'text-danger font-weight-bolder'
    },
    formatPnl(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}`
    },
    formatPct(value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title, icon: params.icon, text: params.text, variant: params.variant,
        },
      })
    },
  },
}
</script>
